import {
  Activity,
  Calendar,
  Car,
  Coins,
  CreditCard,
  Grid3x3,
  HelpCircle,
  Image as ImageIcon,
  MapPin,
  MessageSquare,
  MessageSquarePlus,
  Package,
  Receipt,
  ServerCog,
  Settings,
  Sparkles,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router';
import { useSidebar } from '../context/SidebarContext';
import { ChevronDownIcon, HorizontaLDots } from '../icons';

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
};

// 대시보드
const dashboardItems: NavItem[] = [
  {
    icon: <Grid3x3 />,
    name: 'Dashboard',
    path: '/',
  },
];

// 예약 운영
const reservationItems: NavItem[] = [
  {
    name: '예약 관리',
    icon: <Calendar />,
    path: '/schedule',
  },
  {
    name: '차량 관리',
    icon: <Car />,
    path: '/product',
  },
];

// 딜러 관리
const memberItems: NavItem[] = [
  {
    icon: <Users />,
    name: '딜러 목록 ',
    path: '/dealer',
  },
  {
    name: '딜러 후기',
    icon: <MessageSquare />,
    path: '/review',
  },
  {
    name: '딜러 신고',
    icon: <MessageSquarePlus />,
    path: '/report',
  },
  {
    name: '매매 단지',
    icon: <MapPin />,
    path: '/dealer-location',
  },
  {
    name: 'AI 소개글',
    icon: <Sparkles />,
    path: '/ai-introduction',
  },
  {
    name: 'AI 이미지',
    icon: <ImageIcon />,
    path: '/ai-image',
  },
];

// 정산 관리
const financialItems: NavItem[] = [
  {
    name: '결제 목록',
    icon: <Receipt />,
    path: '/payment',
  },
  {
    name: '결제카드관리',
    icon: <CreditCard />,
    path: '/card',
  },
  {
    name: '딜러 포인트',
    icon: <Coins />,
    path: '/point',
  },
];

// 서비스 운영
const serviceItems: NavItem[] = [
  {
    icon: <Package />,
    name: '금융 & 포인트',
    path: '/service',
  },
  {
    icon: <HelpCircle />,
    name: '고객센터',
    subItems: [
      { name: '공지사항', path: '/notice', pro: false },
      { name: 'FAQ', path: '/faq', pro: false },
    ],
  },
  {
    icon: <Settings />,
    name: '환경설정',
    subItems: [{ name: '약관 관리', path: '/settings', pro: false }],
  },
];

// 시스템 관리
const systemItems: NavItem[] = [
  {
    name: '활동 로그',
    icon: <Activity />,
    path: '/activity-log',
  },
  {
    name: '시스템 정보',
    icon: <ServerCog />,
    path: '/system-info',
  },
];

// 메뉴 섹션 정의
const menuSections = [
  {
    id: 'dashboard',
    title: '대시보드',
    items: dashboardItems,
  },
  {
    id: 'member',
    title: '딜러 관리',
    items: memberItems,
  },
  {
    id: 'reservation',
    title: '예약 운영',
    items: reservationItems,
  },
  {
    id: 'financial',
    title: '정산 관리',
    items: financialItems,
  },
  {
    id: 'service',
    title: '서비스 운영',
    items: serviceItems,
  },
  {
    id: 'system',
    title: '시스템 관리',
    items: systemItems,
  },
];

const AppSidebar: React.FC = () => {
  const location = useLocation();
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();

  const [openSubmenu, setOpenSubmenu] = useState<{
    sectionId: string;
    index: number;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>({});
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const menuButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const isActive = useCallback(
    (path: string) => {
      if (path === '/') return location.pathname === '/';
      return (
        location.pathname === path ||
        location.pathname.startsWith(path + '/') ||
        location.pathname.startsWith(path.replace(/\/$/, '') + 's/')
      );
    },
    [location.pathname],
  );

  // 모바일에서 사이드바 열릴 때 배경 스크롤 방지
  useEffect(() => {
    if (isMobileOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMobileOpen]);

  useEffect(() => {
    let submenuMatched = false;
    menuSections.forEach((section) => {
      section.items.forEach((nav, index) => {
        if (nav.subItems) {
          nav.subItems.forEach((subItem) => {
            if (isActive(subItem.path)) {
              setOpenSubmenu({
                sectionId: section.id,
                index,
              });
              submenuMatched = true;
            }
          });
        }
      });
    });

    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [isActive]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.sectionId}-${openSubmenu.index}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
  }, [openSubmenu]);

  // 서브메뉴 열릴 때 스크롤 자동 조정 (개선된 버전)
  useEffect(() => {
    if (scrollContainerRef.current && openSubmenu !== null) {
      const key = `${openSubmenu.sectionId}-${openSubmenu.index}`;
      const menuButton = menuButtonRefs.current[key];
      const subMenu = subMenuRefs.current[key];

      // 애니메이션 완료 후 실행
      const timer = setTimeout(() => {
        if (scrollContainerRef.current && menuButton && subMenu) {
          const container = scrollContainerRef.current;
          const buttonRect = menuButton.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const subMenuHeight = subMenu.scrollHeight;

          // 버튼 위치 (컨테이너 기준)
          const buttonTop = buttonRect.top - containerRect.top + container.scrollTop;
          const buttonBottom = buttonTop + buttonRect.height + subMenuHeight;

          // 컨테이너의 보이는 영역
          const containerScrollTop = container.scrollTop;
          const containerScrollBottom = containerScrollTop + container.clientHeight;

          // 여유 공간 설정 (화면 크기에 따라 조정)
          const padding = window.innerHeight < 700 ? 40 : 20;

          // 서브메뉴 전체가 보이도록 스크롤 조정
          if (buttonBottom > containerScrollBottom - padding) {
            // 서브메뉴 하단이 화면 아래로 벗어나는 경우
            const scrollTarget = Math.min(
              buttonBottom - container.clientHeight + padding,
              container.scrollHeight - container.clientHeight,
            );

            container.scrollTo({
              top: scrollTarget,
              behavior: 'smooth',
            });
          } else if (buttonTop < containerScrollTop + padding) {
            // 메뉴 버튼이 화면 위로 벗어나는 경우
            container.scrollTo({
              top: Math.max(0, buttonTop - padding),
              behavior: 'smooth',
            });
          }

          // 스크롤 컨테이너 강제 리플로우
          container.style.overflow = 'hidden';
          void container.offsetHeight;
          container.style.overflow = '';
        }
      }, 100); // 애니메이션 시간에 맞춰 약간 지연

      return () => clearTimeout(timer);
    }
  }, [openSubmenu]);

  const handleSubmenuToggle = (index: number, sectionId: string) => {
    const newState = openSubmenu?.sectionId === sectionId && openSubmenu.index === index ? null : { sectionId, index };

    setOpenSubmenu(newState);
  };

  const renderMenuItems = (items: NavItem[], sectionId: string) => (
    <ul className='flex flex-col gap-1'>
      {items.map((nav, index) => (
        <li key={nav.name}>
          {nav.subItems ? (
            <button
              ref={(el) => {
                menuButtonRefs.current[`${sectionId}-${index}`] = el;
              }}
              onClick={() => handleSubmenuToggle(index, sectionId)}
              className={`menu-item group ${
                openSubmenu?.sectionId === sectionId && openSubmenu?.index === index
                  ? 'menu-item-active'
                  : 'menu-item-inactive'
              } cursor-pointer ${!isExpanded && !isHovered ? 'lg:justify-center' : 'lg:justify-start'}`}>
              <span
                className={`menu-item-icon-size  ${
                  openSubmenu?.sectionId === sectionId && openSubmenu?.index === index
                    ? 'menu-item-icon-active'
                    : 'menu-item-icon-inactive'
                }`}>
                {nav.icon}
              </span>
              {(isExpanded || isHovered || isMobileOpen) && <span className='menu-item-text'>{nav.name}</span>}
              {(isExpanded || isHovered || isMobileOpen) && (
                <ChevronDownIcon
                  className={`ml-auto w-5 h-5 transition-transform duration-200 ${
                    openSubmenu?.sectionId === sectionId && openSubmenu?.index === index
                      ? 'rotate-180 text-brand-500'
                      : ''
                  }`}
                />
              )}
            </button>
          ) : (
            nav.path && (
              <Link
                to={nav.path}
                onClick={() => setOpenSubmenu(null)}
                className={`menu-item group ${isActive(nav.path) ? 'menu-item-active' : 'menu-item-inactive'}`}>
                <span
                  className={`menu-item-icon-size ${
                    isActive(nav.path) ? 'menu-item-icon-active' : 'menu-item-icon-inactive'
                  }`}>
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && <span className='menu-item-text'>{nav.name}</span>}
              </Link>
            )
          )}
          {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
            <div
              ref={(el) => {
                subMenuRefs.current[`${sectionId}-${index}`] = el;
              }}
              className='overflow-hidden transition-all duration-300'
              style={{
                height:
                  openSubmenu?.sectionId === sectionId && openSubmenu?.index === index
                    ? `${subMenuHeight[`${sectionId}-${index}`]}px`
                    : '0px',
              }}>
              <ul className='mt-2 space-y-1 ml-9'>
                {nav.subItems.map((subItem) => (
                  <li key={subItem.name}>
                    <Link
                      to={subItem.path}
                      className={`menu-dropdown-item ${
                        isActive(subItem.path) ? 'menu-dropdown-item-active' : 'menu-dropdown-item-inactive'
                      }`}>
                      {subItem.name}
                      <span className='flex items-center gap-1 ml-auto'>
                        {subItem.new && (
                          <span
                            className={`ml-auto ${
                              isActive(subItem.path) ? 'menu-dropdown-badge-active' : 'menu-dropdown-badge-inactive'
                            } menu-dropdown-badge`}>
                            new
                          </span>
                        )}
                        {subItem.pro && (
                          <span
                            className={`ml-auto ${
                              isActive(subItem.path) ? 'menu-dropdown-badge-active' : 'menu-dropdown-badge-inactive'
                            } menu-dropdown-badge`}>
                            pro
                          </span>
                        )}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <>
      {/* 사이드바 */}
      <aside
        className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-5 left-0 bg-white dark:bg-gray-900 dark:border-gray-700 text-gray-900 h-screen transition-all duration-300 ease-in-out z-50 border-r border-gray-200 
        ${isExpanded || isMobileOpen ? 'w-[190px]' : isHovered ? 'w-[190px]' : 'w-[90px]'}
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        overflow-hidden`}
        onMouseEnter={() => !isExpanded && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}>
        {/* 로고 영역 */}
        <div
          className={`py-8 hidden lg:flex flex-shrink-0 ${
            !isExpanded && !isHovered ? 'lg:justify-center' : 'justify-start'
          }`}>
          <Link to='/' className='flex items-center gap-2'>
            {isExpanded || isHovered || isMobileOpen ? (
              <>
                <img
                  className='dark:hidden drop-shadow-[0_0_1px_rgba(0,0,0,0.8)]'
                  src='/images/logo/logo.png'
                  alt='Logo'
                  width={140}
                  height={60}
                />
                <img className='hidden dark:block' src='/images/logo/logo.png' alt='Logo' width={140} height={60} />
              </>
            ) : (
              <img src='/images/user/master.avif' alt='Logo' width={32} height={32} />
            )}
          </Link>
        </div>

        {/* 메뉴 영역 - 스크롤 가능 */}
        <div
          ref={scrollContainerRef}
          className='flex flex-col flex-1 overflow-y-auto overflow-x-hidden duration-300 ease-linear scrollbar-hide mt-6 lg:mt-0'
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}>
          <nav className='mb-6 pb-20'>
            {/* pb-20으로 충분한 하단 여유 공간 확보 */}
            <div className='flex flex-col gap-4'>
              {menuSections.map((section) => (
                <div key={section.id}>
                  <h2
                    className={`mb-4 text-xs uppercase flex leading-[20px] text-gray-400 ${
                      !isExpanded && !isHovered ? 'lg:justify-center' : 'justify-start'
                    }`}>
                    {isExpanded || isHovered || isMobileOpen ? section.title : <HorizontaLDots className='size-6' />}
                  </h2>
                  {renderMenuItems(section.items, section.id)}
                </div>
              ))}
            </div>
          </nav>
          <div className='pb-10'></div>
        </div>
      </aside>
    </>
  );
};

export default AppSidebar;
